import { React, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import styled, { useTheme } from 'styled-components';
import { Ticket } from 'lucide-react';
import { useAppContext } from '../../appprovider';
import { redeemInstructorActivationCode } from '../services/instructorActivationCodeApi';
import {
  Sheet,
  CloseWrap,
  Head,
  Title,
  Sub,
  Cta,
  activationCodeDialogPaperProps,
  getPlanTierDialogBackdropProps,
} from './planTierPresentation';
import { Field, Input } from './primitives';
import { formatPlanLabel } from '../admin/Packages/planDisplayUtils';

/** Card sheet — white surface, not page mint `theme.bg` from shared Sheet. */
const ActivationSheet = styled(Sheet)`
  background: ${({ theme }) => theme.surface};
`;

const FormBody = styled.div`
  max-width: 360px;
  margin: 0 auto;
  padding-top: 4px;
`;

const CodeField = styled(Field)`
  margin-bottom: 0;
`;

const CodeInputWrap = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.textMuted};
    pointer-events: none;
  }
`;

const CodeInput = styled(Input)`
  padding-left: 40px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-color: ${({ theme, $error }) => ($error ? theme.danger : undefined)};

  &:focus {
    border-color: ${({ theme, $error }) => ($error ? theme.danger : theme.accent)};
    box-shadow: 0 0 0 3px ${({ theme, $error }) => (
      $error
        ? `color-mix(in oklch, ${theme.danger} 18%, transparent)`
        : theme.accentSoft
    )};
  }
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.danger};
  font-weight: 600;
`;

const ApplyWrap = styled.div`
  margin-top: 18px;
`;

const Hint = styled.p`
  margin: 12px 0 0;
  font-size: ${({ theme }) => theme.fsMicro};
  line-height: 1.45;
  color: ${({ theme }) => theme.textMuted};
  text-align: center;
`;

/**
 * Modal to redeem an instructor activation code — matches plan-tier dialog styling.
 */
export default function ActivationCodeDialog({
  open,
  onClose,
  onSuccess,
}) {
  const theme = useTheme();
  const { userId, loadEntitlements } = useAppContext();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCode('');
    setError('');
    setLoading(false);
  }, [open]);

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter an activation code');
      return;
    }
    const resolvedUserId = userId || localStorage.getItem('userId');
    if (!resolvedUserId) {
      setError('You must be signed in to redeem a code');
      return;
    }

    setError('');
    setLoading(true);
    try {
      // The code itself dictates monthly vs yearly on the backend, so the requested
      // cycle here is ignored — pass a stable default.
      const result = await redeemInstructorActivationCode(
        resolvedUserId,
        trimmed,
        false,
      );
      const tier = result?.planId || result?.entitlements?.tier;
      if (tier) {
        localStorage.setItem('studyak_plan_tier', tier);
        localStorage.setItem('packageIdplan', tier);
        localStorage.setItem('name', formatPlanLabel(tier));
      }
      await loadEntitlements(resolvedUserId);
      if (onSuccess) {
        onSuccess(result);
      }
      onClose();
    } catch (applyError) {
      setError(applyError.message || 'Unable to redeem activation code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      PaperProps={activationCodeDialogPaperProps}
      slotProps={{ backdrop: getPlanTierDialogBackdropProps(theme) }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <ActivationSheet>
          <CloseWrap>
            <IconButton
              onClick={onClose}
              aria-label="Close activation code dialog"
              size="small"
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>
          </CloseWrap>

          <Head>
            <Title>Redeem activation code</Title>
            <Sub>
              Enter your code to unlock an instructor plan. The plan and billing term are set by the code.
            </Sub>
          </Head>

          <FormBody>
            <CodeField>
              <label htmlFor="instructor-activation-code">Activation code</label>
              <CodeInputWrap>
                <Ticket size={16} strokeWidth={2} />
                <CodeInput
                  id="instructor-activation-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleApply();
                    }
                  }}
                  placeholder="Enter code"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  disabled={loading}
                  $error={!!error}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'activation-code-error' : undefined}
                />
              </CodeInputWrap>
              {error ? (
                <ErrorText id="activation-code-error" role="alert">{error}</ErrorText>
              ) : null}
            </CodeField>

            <ApplyWrap>
              <Cta
                type="button"
                $bg={theme.accent}
                $fg={theme.onAccent}
                onClick={handleApply}
                disabled={loading}
              >
                <span>{loading ? 'Applying…' : 'Apply code'}</span>
              </Cta>
            </ApplyWrap>

            <Hint>Codes are case-insensitive. Your plan updates immediately after a successful redeem.</Hint>
          </FormBody>
        </ActivationSheet>
      </DialogContent>
    </Dialog>
  );
}
